import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnInit,
    Self,
    viewChild,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    FormsModule,
    NgControl,
    ReactiveFormsModule,
} from '@angular/forms';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {ReportingService} from 'src/app/reporting/reporting.service';

@Component({
    selector: 'app-input-search',
    templateUrl: './input-search.component.html',
    styleUrls: ['./input-search.component.scss'],
    imports: [ReactiveFormsModule, FormsModule, NgbModule],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputSearchComponent implements OnInit, ControlValueAccessor {
    @Input() placeholder = '';
    @Input() autoFocus = false;
    @Input() name = '';
    @Input() autoComplete: 'on' | 'off' = 'off';

    private readonly searchInput = viewChild.required<ElementRef<HTMLInputElement>>('searchInput');
    formControl = new FormControl<string>('', {nonNullable: true});

    onTouch: (() => void) | undefined;

    private onChange: ((value: string | null) => void) | undefined;
    constructor(
        @Self() public ngControl: NgControl,
        private changeDetectorRef: ChangeDetectorRef,
        private reportingService: ReportingService,
    ) {
        ngControl.valueAccessor = this;
    }

    @HostListener('document:keydown', ['$event'])
    private handleKeyboardEvent(event: KeyboardEvent): void {
        if (event.key === 'Escape' && event.shiftKey) {
            this.setFocus();
            event.preventDefault();
        }
    }

    registerOnChange(fn: (value: string | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouch = fn;
    }

    writeValue(value: string): void {
        this.formControl.setValue(value);
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.formControl.disable();
        } else {
            this.formControl.enable();
        }
    }

    setFocus(): void {
        this.searchInput().nativeElement.focus();
    }

    ngOnInit(): void {
        this.formControl.valueChanges.subscribe(data => {
            this.reportingService.setSearchString(data ?? '');
            this.onChange?.(data);
        });

        // Then using ChangeDetectionStrategy.OnPush the component doesn't notice, when the control is marked as
        // touched, and will therefore not show validation errors. However, Angular doesn't offer a simple way of
        // listening to, when a control is marked as touched, that we can hook into. So we have to reassign the
        // markAsTouched() method in order to call detectChanges().
        if (this.ngControl.control != null) {
            const originalMarkAsTouched = this.ngControl.control.markAsTouched.bind(
                this.ngControl.control,
            );
            const changeDetectorRef = this.changeDetectorRef;
            this.ngControl.control.markAsTouched = function (...args): void {
                originalMarkAsTouched.apply(this, args);
                changeDetectorRef.detectChanges();
            };
        }
    }
}
